import { Address, IViewUser, SaferAddress } from '@/@types/generated'
import { format as dateFns, formatRelative, parseISO } from 'date-fns'
import { formatEnum } from './enum'

// Import custom formatters to register them
import './ApplicationRejectionStep'
import './BusinessType'
import './CaseNoticeType'
import './CaseSanctionType'
import './CaseStatus'
import './ChameleonMatchType'
import './ChameleonQueueStatus'
import './ChargeFlag'
import './ChargeRelatedKind'
import './ChargeType'
import './HazmatOverrideEventType'
import './HazmatOverrideSource'
import './HazmatPlacardingStatus'
import './InsurerStatus'
import './LicensingAgencyDocumentType'
import './LicensingAgencyStatus'
import './MccsRoles'
import './PaymentMethod'
import './Region'
import './RegistrationPeriod'
import './RegistrationType'
import './TariffSection'
import './VehicleReviewRequestStatus'
import './VehicleStatus'

function formatPhoneNumber(phoneNumber?: string | null): string {
  if (!phoneNumber) return '—'

  // Remove all non-digit characters
  const cleaned = phoneNumber.replace(/\D/g, '')

  // Format 1-800* numbers
  if (cleaned.length === 11 && cleaned[0] === '1')
    return `1-${cleaned.slice(1, 4)}-${cleaned.slice(4, 7)}-${cleaned.slice(7)}`

  // Otherwise, fallback
  if (cleaned.length !== 10) return phoneNumber

  return `(${cleaned.slice(0, 3)}) ${cleaned.slice(3, 6)}-${cleaned.slice(6)}`
}

function formatAddress(addr?: SaferAddress): string
function formatAddress(addr?: Address | SaferAddress): string {
  if (!addr) return '\u2014'
  return `${addr.street1}${
    addr.street2 ? `, ${addr.street2}` : ''
  }, ${addr.city}, ${addr.stateProvince} ${addr.postalCode}`
}

function formatDate(isoString?: string | null): string {
  if (!isoString) return '\u2014'
  const date = parseISO(isoString)
  return dateFns(date, 'MMMM d, yyyy')
}

function formatDateTime(isoString?: string | null, relative?: boolean): string {
  if (!isoString) return '\u2014'

  const date = parseISO(isoString)
  return !relative
    ? dateFns(date, 'MMMM d, yyyy h:mm a')
    : formatRelative(date, new Date())
}

function formatCarrierName(carrier?: {
  legalName?: string | null
  businessName?: string | null
  dbaName?: string | null
}): string {
  if (!carrier) return '\u2014'
  const businessName = carrier.businessName ?? carrier.dbaName
  return businessName
    ? `${carrier.legalName} dba ${businessName}`
    : carrier.legalName || '\u2014'
}

function formatUserName(
  user?: Pick<IViewUser, 'firstName' | 'lastName'> | null
) {
  if (!user) return '\u2014'
  return `${user.firstName} ${user.lastName}`
}

function formatNumber(n: number | null | undefined, decimals = 0): string {
  if (n === null || n === undefined) return '\u2014'
  return n.toLocaleString('en-US', {
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals,
  })
}

function formatCurrency(
  n: number | null | undefined,
  decimals = 2,
  currency = 'USD'
): string {
  if (n === null || n === undefined) return '\u2014'
  return n.toLocaleString('en-US', {
    style: 'currency',
    currency: currency,
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals,
  })
}

function formatFileSize(bytes: number | null | undefined): string {
  if (bytes === null || bytes === undefined) return '\u2014'
  const sizes = ['Bytes', 'KB', 'MB', 'GB', 'TB']
  if (bytes === 0) return '0 Byte'
  const i = Math.floor(Math.log(bytes) / Math.log(1024))
  const decimals = sizes[i] === 'Bytes' ? 0 : 2
  return `${(bytes / Math.pow(1024, i)).toFixed(decimals)} ${sizes[i]}`
}

function formatMiddleEllipsis(text?: string | null, maxLength = 15): string {
  if (!text) return '\u2014'
  if (text.length <= maxLength) return text
  const half = Math.floor(maxLength / 2)
  return `${text.slice(0, half)}…${text.slice(text.length - half)}`
}

const format = {
  Address: formatAddress,
  CarrierName: formatCarrierName,
  Currency: formatCurrency,
  Date: formatDate,
  DateTime: formatDateTime,
  Enum: formatEnum,
  FileSize: formatFileSize,
  MiddleEllipsis: formatMiddleEllipsis,
  PhoneNumber: formatPhoneNumber,
  Number: formatNumber,
  UserName: formatUserName,
}

export default format
